import { useState, useMemo } from 'react';
import * as Popover from '@radix-ui/react-popover';
import { cn } from '@/utils/cn';
import { SelectChevron } from '@/components/ui/Select';
import { filterTriggerFocus } from '@/components/ui/filterTriggerStyles';
import { ICONS } from '@/constants/icons';
import { Icon } from '@/components/ui/Icon';

/**
 * IconPicker Component
 *
 * Trigger button that opens a searchable popover grid of HappyIcons.
 * Mirrors the Radix Popover pattern used by ColorPicker.
 */

interface IconPickerProps {
  value: string | null;
  onChange: (value: string | null) => void;
  className?: string;
}

// Deduplicated list of all icon values from the ICONS constant
const ALL_ICONS: string[] = Array.from(new Set(Object.values(ICONS)));

export const IconPicker = ({ value, onChange, className }: IconPickerProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');

  const filtered = useMemo(() => {
    if (!search.trim()) return ALL_ICONS;
    const q = search.toLowerCase();
    // Search against the ICONS keys (human-readable names) rather than raw values
    const entries = Object.entries(ICONS) as [string, string][];
    const matched = new Set<string>();
    for (const [key, val] of entries) {
      if (key.toLowerCase().includes(q) || val.toLowerCase().includes(q)) {
        matched.add(val);
      }
    }
    return ALL_ICONS.filter((v) => matched.has(v));
  }, [search]);

  const handleSelect = (iconName: string) => {
    onChange(iconName);
    setIsOpen(false);
    setSearch('');
  };

  const handleClear = () => {
    onChange(null);
    setIsOpen(false);
    setSearch('');
  };

  return (
    <div className={cn('w-full', className)}>
      <Popover.Root open={isOpen} onOpenChange={(open) => { setIsOpen(open); if (!open) setSearch(''); }}>
        <Popover.Trigger asChild>
          <button
            type="button"
            className={cn(
              'w-full h-[34px] lg:h-[42px] rounded-lg border border-gray-200',
              'flex items-center gap-3 px-3',
              filterTriggerFocus,
              'transition-[border-color,background-color,color,opacity] duration-200 hover:border-gray-300',
              isOpen && 'border-primary-400'
            )}
            aria-label={value ? `Selected icon: ${value}` : 'Pick an icon'}
          >
            {value ? (
              <>
                <span className="flex-shrink-0 w-6 h-6 flex items-center justify-center">
                  <Icon name={value} size="sm" />
                </span>
                <span className="text-sm text-gray-700 flex-1 text-left font-mono truncate">{value}</span>
              </>
            ) : (
              <>
                <span className="flex-shrink-0 w-6 h-6 flex items-center justify-center text-gray-300">
                  <Icon name="star" size="sm" />
                </span>
                <span className="text-sm text-gray-400 flex-1 text-left">Pick icon…</span>
              </>
            )}
            <SelectChevron className="shrink-0 text-gray-500" />
          </button>
        </Popover.Trigger>

        <Popover.Portal>
          <Popover.Content
            className={cn(
              'bg-white rounded-xl shadow-xl border border-gray-200',
              'p-3 z-[200] w-72 sm:w-[22rem]',
              'data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95',
              'data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95'
            )}
            sideOffset={8}
            align="start"
            onCloseAutoFocus={(e) => e.preventDefault()}
          >
            {/* Search */}
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search icons…"
              className="w-full h-8 px-2 mb-2 text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent placeholder:text-gray-400"
              autoFocus
            />

            {/* Icon Grid */}
            <div className="grid grid-cols-6 sm:grid-cols-7 gap-1 max-h-48 overflow-y-auto overflow-x-hidden">
              {filtered.map((iconName) => (
                <button
                  key={iconName}
                  type="button"
                  onClick={() => handleSelect(iconName)}
                  title={iconName}
                  className={cn(
                    'w-full aspect-square flex items-center justify-center rounded-lg transition-all duration-150',
                    'hover:bg-gray-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500',
                    value === iconName && 'bg-primary-100 ring-2 ring-primary-500'
                  )}
                  aria-label={`Select icon ${iconName}`}
                  aria-pressed={value === iconName}
                >
                  <Icon name={iconName} size="sm" />
                </button>
              ))}
              {filtered.length === 0 && (
                <span className="col-span-6 sm:col-span-7 text-xs text-gray-400 text-center py-4">No icons found</span>
              )}
            </div>

            {/* Clear button */}
            <div className="mt-2 pt-2 border-t border-gray-100">
              <button
                type="button"
                onClick={handleClear}
                className="w-full text-xs text-gray-500 hover:text-gray-700 py-1 hover:bg-gray-50 rounded-md transition-colors"
              >
                Clear icon
              </button>
            </div>

            <Popover.Arrow className="fill-white" />
          </Popover.Content>
        </Popover.Portal>
      </Popover.Root>
    </div>
  );
};
